import { useMutation, useQueryClient } from '@tanstack/react-query'
import { signIn, signOut } from 'aws-amplify/auth'

import { queries } from 'src/lib/query-keys'
import { sendEvent } from 'src/shared/api/events/send-event'

export const useSignIn = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      // Clear any existing auth sessions to prevent "already signed in" errors
      try {
        await signOut({ global: true })
      } catch (signOutError) {
        // Ignore errors during sign out as the user might not be signed in
        console.warn('Sign-out step:', signOutError instanceof Error ? signOutError.message : 'Unknown error')
      }

      const result = await signIn({ username: email, password })
      return { result, email }
    },
    onSuccess: async ({ result, email }) => {
      if (result.isSignedIn) {
        // First invalidate auth queries to refresh the auth state
        await queryClient.invalidateQueries({ queryKey: queries.auth._def })

        // Then invalidate user data queries to fetch fresh data
        await queryClient.invalidateQueries({ queryKey: queries.users.me.queryKey })

        // Track successful sign in
        sendEvent({ eventType: 'auth_signin_success', info: { email } })
      }
    },
    onError: (error: unknown) => {
      const errorName = error instanceof Error ? error.name : 'unknown'
      sendEvent({
        eventType: 'auth_signin_error',
        info: { error: errorName },
      })
    },
  })
}
